<template>
  <WebhookForm :id="id" :initial="effectiveInitial" :visibility-config="visibilityConfig" @saved="$emit('saved')">
    <template #parameters="{ getSecret, setSecret, getParam, setParam }">
      <div class="box">
        <h2 class="subtitle">{{ $t('openai.settings') }}</h2>

        <BFieldHint :label="$t('openai.model')" hintKey="">
          <b-input
            :model-value="getParam('MODEL')"
            :placeholder="$t('openai.modelPlaceholder')"
            @update:model-value="val => setParam('MODEL', val)"
          />
        </BFieldHint>

        <BFieldHint :label="$t('openai.system')" hintKey="">
          <b-input
            type="textarea"
            :rows="3"
            :model-value="getParam('SYSTEM')"
            :placeholder="$t('openai.systemPlaceholder')"
            @update:model-value="val => setParam('SYSTEM', val)"
          />
        </BFieldHint>

        <BFieldHint :label="$t('openai.maxTokens')" hintKey="">
          <b-input
            type="number"
            min="1"
            :model-value="getParam('MAX_TOKENS')"
            :placeholder="$t('openai.maxTokensPlaceholder')"
            @update:model-value="val => setParam('MAX_TOKENS', String(val))"
          />
        </BFieldHint>

        <BFieldHint :label="$t('openai.temperature')" hintKey="">
          <b-input
            type="number"
            min="0"
            max="2"
            step="0.1"
            :model-value="getParam('TEMPERATURE')"
            :placeholder="$t('openai.temperaturePlaceholder')"
            @update:model-value="val => setParam('TEMPERATURE', String(val))"
          />
        </BFieldHint>

        <BFieldHint :label="$t('openai.apiKey')" hintKey="">
          <b-select :model-value="getSecret('OPENAI_API_KEY')"
                    :placeholder="$t('openai.selectSecret')"
                    @update:model-value="val => setSecret('OPENAI_API_KEY', String(val))">
            <option v-for="name in secretOptions" :key="name" :value="name">{{ name }}</option>
          </b-select>
        </BFieldHint>
      </div>
    </template>
  </WebhookForm>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'
import WebhookForm from '@/views/templates/WebhookForm.vue'
import BFieldHint from '@/components/BFieldHint.vue'
import { useStore } from 'vuex'

type Param = { template_name: string; value: string }

export default defineComponent({
  name: 'OpenAIForm',
  components: { WebhookForm, BFieldHint },
  props: {
    id: String,
    initial: Object as PropType<Record<string, unknown>>
  },
  emits: ['saved'],
  setup(props) {
    const store = useStore()
    const secretOptions = computed<string[]>(() => {
      const s: any = store.state.secrets || []
      const list = Array.isArray(s) ? s : Object.values(s)
      return list.map((x: any) => String(x.name)).filter(Boolean)
    })

    const defaultInitial = {
      name: '',
      event: 'chat',
      parameters: [
        { template_name: 'MODEL', value: 'gpt-4o' },
        { template_name: 'SYSTEM', value: 'You are a helpful assistant.' },
        { template_name: 'MAX_TOKENS', value: '30' },
        { template_name: 'TEMPERATURE', value: '0.8' }
      ],
      template: 'openai-chat',
      url: 'https://api.openai.com/v1/chat/completions',
      requestType: 'POST',
      noDefaults: true,
      rejectUnauthorized: true,
      responseTemplate: '{{{choices.0.message.content}}}',
      unchunked: true,
      data_url_response_event: false,
      headers: {
        'Authorization': 'Bearer {{{OPENAI_API_KEY}}}'
      },
      json: {
        model: '{{{MODEL}}}',
        messages: [
          { role: 'system', content: '{{{SYSTEM}}}' },
          { role: 'user', content: '{{{FERMION_EVENT_VALUE}}}' }
        ],
        max_tokens: '{{{MAX_TOKENS}}}',
        temperature: '{{{TEMPERATURE}}}'
      }
    } as Record<string, unknown>

    const effectiveInitial = computed<Record<string, unknown>>(() => {
      return (props.initial as any) || defaultInitial
    })
    
    const visibilityConfig = {
      jsonTab: false,
      secrets: false,
      requestURL: false,
      requestType: false,
      requestFormat: true,
      requestFormatForm: false,
      requestFormatCustom: false,
      requestFormatJsonDefault: false,
      queryParams: false,
      httpHeaders: false,
      httpBasicAuth: false,
      responseTemplate: false
    }
    
    return { secretOptions, effectiveInitial, visibilityConfig }
  }
})
</script>
